import { constants } from '../../App.constants'

import axios from 'axios';

/**
 * The add-on renewal flow (add-on renewal spec, section 6). Every call carries
 * the member's login token; the API decides who they are from it. Callbacks
 * get (err, data). err is null on success, otherwise
 * { status, code, message, docusignStatus } so the flow can tell a 409 from a
 * 502.
 */

const config = (loginToken) => ({
    headers: {
        'Content-Type': 'application/json',
        'login-token': loginToken
    }
})

const renewalUrl = (renewalId, suffix = '') =>
    constants.dashboardBaseUrl + '/organization/' + constants.selectedOrg + '/addOnRenewal/' + encodeURIComponent(renewalId) + suffix

const failure = (error) => {
    const res = error && error.response
    const body = (res && res.data) || {}
    return {
        status: res ? res.status : 0,
        code: body.code || null,
        message: body.message || (error && error.message) || 'error',
        docusignStatus: body.docusignStatus
    }
}

// The renewal endpoints answer { error: true, message } on failure. Some
// (the GET) do it with HTTP 200, so the body is checked too.
const settle = async (request, cb, pick = (body) => body.data) => {
    try {
        const payload = await request()
        const body = (payload && payload.data) || {}
        if (body.error) {
            return cb({ status: payload.status || 0, code: body.code || null, message: body.message || 'error', docusignStatus: body.docusignStatus })
        }
        cb(null, pick(body))
    } catch (error) {
        cb(failure(error))
    }
}

export const getAddOnRenewal = (loginToken, renewalId, cb) => {
    return async () => settle(() => axios.get(renewalUrl(renewalId), config(loginToken)), cb)
}

/** lastStepReached, for the follow-up emails. Best effort: failures are ignored. */
export const recordAddOnRenewalStep = (loginToken, renewalId, step, cb = () => {}) => {
    return async () => settle(() => axios.put(renewalUrl(renewalId, '/step'), { step }, config(loginToken)), cb)
}

/** { hasDefaultPaymentMethod, card: { brand, last4 } | null } */
export const getAddOnPaymentMethod = (loginToken, renewalId, cb) => {
    return async () => settle(() => axios.get(renewalUrl(renewalId, '/payment-method'), config(loginToken)), cb)
}

/** { clientSecret, setupIntentId, connectedAccountId, hasDefaultPaymentMethod } */
export const createAddOnSetupIntent = (loginToken, renewalId, cb) => {
    return async () => settle(() => axios.post(renewalUrl(renewalId, '/setup-intent'), {}, config(loginToken)), cb)
}

/** After Stripe.js confirmed the SetupIntent: make that card the default. */
export const setAddOnDefaultPaymentMethod = (loginToken, renewalId, setupIntentId, cb) => {
    return async () => settle(() => axios.post(renewalUrl(renewalId, '/payment-method'), { setupIntentId }, config(loginToken)), cb)
}

/**
 * The embedded DocuSign URL for the add-on's renewal contract. The server
 * picks the template, the signer's name and email and the identity fields of
 * the prefill from the renewal, so only the renewal and the org are sent -
 * and no x-user-id: the renewal path signs with the org's connection.
 * Resolves { envelopeId, url }.
 */
export const addOnRenewalEmbeddedUrl = (loginToken, { renewalId, orgId, returnUrl, signer1, prefill }, cb) => {
    return async () => {
        const data = { renewalId, orgId }
        if (returnUrl) { data.returnUrl = returnUrl }
        if (signer1) { data.signer1 = signer1 }
        if (prefill) { data.prefill = prefill }

        settle(() => axios.post(constants.dashboardBaseUrl + '/oauth/docusign/embedded-url', data, config(loginToken)), cb, (body) => body)
    }
}

/** { renewalId, status: 'signed', signedAt }; 409 { docusignStatus } when not yet. */
export const verifyAddOnRenewalSignature = (loginToken, renewalId, cb) => {
    return async () => settle(() => axios.post(renewalUrl(renewalId, '/verify-signature'), {}, config(loginToken)), cb)
}

/** { renewalId, status: 'enrolled', mode, payer, ..., alreadyEnrolled } */
// billing ('invoice' | 'card') is sent for self-pay only.
export const enrollAddOnRenewal = (loginToken, renewalId, payer, cb, billing) => {
    const body = billing ? { payer, billing } : { payer }
    return async () => settle(() => axios.post(renewalUrl(renewalId, '/enroll'), body, config(loginToken)), cb)
}
